import maplibregl from "maplibre-gl";
import { useEffect, useRef, useState } from "react";

import "maplibre-gl/dist/maplibre-gl.css";

export type Pin = {
  lat: number;
  lng: number;
  label?: string;
  kind?: "guess" | "found" | "target";
  /** Overrides the marker colour, e.g. a point on the warmth ramp. */
  color?: string;
};

/**
 * A labelled paper basemap. PRD 12: this is a deduction game, not a
 * map-reading test, so place labels stay on -- making someone hunt visually
 * for Agra on a blank map is a different and worse game.
 *
 * Positron rather than the dark basemap, because the brand kit makes Haze the
 * default surface and a full-bleed night map would fight it. Dusk stays as the
 * ink the map is labelled in.
 */
export type Basemap = "map" | "satellite";

const BASEMAP_KEY = "hidein.basemap";

function storedBasemap(): Basemap {
  try {
    return localStorage.getItem(BASEMAP_KEY) === "satellite" ? "satellite" : "map";
  } catch {
    return "map";
  }
}

/**
 * Two basemaps, both labelled.
 *
 * PRD 12 is firm that labels stay on -- making somebody hunt visually for Agra
 * is a different and worse game -- so satellite mode is a *hybrid*: Esri's
 * imagery under Esri's boundaries-and-places reference layer, the same thing
 * Google means by "Satellite". Bare imagery would quietly turn a deduction
 * game into an eyesight test.
 */
/**
 * Built fresh per map. MapLibre normalises the style object it is handed, so a
 * shared module-level constant gets mutated by the first map and then fails to
 * parse for every map constructed after it — silently, with no error event.
 * Route changes mount several maps per page, so this has to be a factory.
 */
const makeStyle = (): maplibregl.StyleSpecification => ({
  version: 8,
  sources: {
    carto: {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png",
        "https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png",
        "https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png",
      ],
      tileSize: 256,
      attribution:
        '<a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · <a href="https://carto.com/attributions">CARTO</a>',
    },
    satellite: {
      type: "raster",
      tiles: [
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      maxzoom: 18,
      attribution: "Imagery © Esri, Maxar, Earthstar Geographics",
    },
    satlabels: {
      type: "raster",
      tiles: [
        "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      maxzoom: 18,
      attribution: "© Esri",
    },
  },
  layers: [
    { id: "bg", type: "background", paint: { "background-color": "#F3F1FF" } },
    { id: "carto", type: "raster", source: "carto" },
    {
      id: "satellite",
      type: "raster",
      source: "satellite",
      layout: { visibility: "none" },
    },
    {
      id: "satlabels",
      type: "raster",
      source: "satlabels",
      layout: { visibility: "none" },
    },
  ],
});

export type RegionShading = {
  size?: number;
  cells: { lat: number; lng: number; d: number }[];
  /**
   * Late in a round the server stops resolving cells, because a grid over a
   * handful of candidates is a set of coordinates. One box keeps the map
   * saying something during the part of the round it matters most.
   */
  bounds?: {
    min_lat: number;
    max_lat: number;
    min_lng: number;
    max_lng: number;
  } | null;
} | null;

export type Box = {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
};

export function MapView({
  pins,
  dropMode = false,
  onDrop,
  fitTo,
  fitBox = null,
  fullBleed = false,
  layerToggle = false,
  region = null,
}: {
  pins: Pin[];
  dropMode?: boolean;
  onDrop?: (lat: number, lng: number) => void;
  /** Fly to these bounds when they change. */
  fitTo?: Pin[] | null;
  /**
   * Frame a declared arena. A round played inside one country should not open
   * on a view of the Pacific — the board is the first thing the seeker is told,
   * so it should also be the first thing they see.
   */
  fitBox?: Box | null;
  /** Fill the viewport rather than a sized parent. */
  fullBleed?: boolean;
  /** Show the map / satellite switch. */
  layerToggle?: boolean;
  /** Cells where the answer can still be. Shaded, never pinned. */
  region?: RegionShading;
}) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const [basemap, setBasemap] = useState<Basemap>(storedBasemap);
  const markers = useRef<maplibregl.Marker[]>([]);
  const dropRef = useRef(onDrop);
  dropRef.current = onDrop;

  useEffect(() => {
    if (!container.current || map.current) return;
    const m = new maplibregl.Map({
      container: container.current,
      style: makeStyle(),
      center: [10, 22],
      zoom: 1.4,
      attributionControl: { compact: true },
      dragRotate: false,
      maxZoom: 12,
    });
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    m.on("click", (e) => dropRef.current?.(e.lngLat.lat, e.lngLat.lng));
    // A basemap that fails to load is otherwise completely silent.
    m.on("error", (e) => console.error("[map]", e.error?.message ?? e));
    map.current = m;
    return () => {
      m.remove();
      map.current = null;
    };
  }, []);

  // Swap basemap by toggling layer visibility rather than calling setStyle,
  // which would tear down the region shading and every marker with it.
  //
  // Applied on a `styledata` loop rather than a one-shot `load` listener:
  // isStyleLoaded() is not a reliable gate (it can report false on a map that
  // has long since drawn, and a `once("load")` registered after load has
  // already fired never runs at all — which silently stranded the toggle).
  // Retrying until the layer actually exists is correct under every ordering.
  useEffect(() => {
    const m = map.current;
    if (!m) return;

    const apply = () => {
      if (!m.getLayer("carto")) return false;
      const sat = basemap === "satellite";
      m.setLayoutProperty("carto", "visibility", sat ? "none" : "visible");
      m.setLayoutProperty("satellite", "visibility", sat ? "visible" : "none");
      m.setLayoutProperty("satlabels", "visibility", sat ? "visible" : "none");
      return true;
    };

    try {
      localStorage.setItem(BASEMAP_KEY, basemap);
    } catch {
      /* private mode: the choice just does not persist */
    }

    if (apply()) return;
    const onData = () => {
      if (apply()) m.off("styledata", onData);
    };
    m.on("styledata", onData);
    return () => {
      m.off("styledata", onData);
    };
  }, [basemap]);

  // Cursor affordance for drop-pin mode.
  useEffect(() => {
    const canvas = map.current?.getCanvas();
    if (canvas) canvas.style.cursor = dropMode ? "crosshair" : "";
  }, [dropMode]);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    for (const marker of markers.current) marker.remove();
    markers.current = pins.map((pin) => {
      const wrap = document.createElement("div");
      wrap.style.display = "grid";
      wrap.style.justifyItems = "center";

      if (pin.label) {
        const label = document.createElement("div");
        label.className = "marker-label";
        label.textContent = pin.label;
        wrap.appendChild(label);
      }
      const dot = document.createElement("div");
      dot.className = `marker${pin.kind === "found" || pin.kind === "target" ? " found" : ""}`;
      // A guess marker is coloured by how close it landed, so the map itself
      // reads as the warmth ramp.
      if (pin.color) dot.style.background = pin.color;
      wrap.appendChild(dot);

      return new maplibregl.Marker({ element: wrap })
        .setLngLat([pin.lng, pin.lat])
        .addTo(m);
    });
  }, [pins]);

  // Shade where the answer can still be. Chill, because this is the cold end
  // of the ramp: it is the space you have not eliminated yet.
  useEffect(() => {
    const m = map.current;
    if (!m) return;

    const apply = () => {
      const size = region?.size ?? 4;
      const box = region?.cells.length ? null : (region?.bounds ?? null);
      const rings: { d: number; ring: [number, number][] }[] = box
        ? [
            {
              // Mid weight, not full: one box is a shrug about where inside it
              // the answer sits, and the labels underneath still have to read.
              d: 0.45,
              ring: [
                [box.min_lng, box.min_lat],
                [box.max_lng, box.min_lat],
                [box.max_lng, box.max_lat],
                [box.min_lng, box.max_lat],
                [box.min_lng, box.min_lat],
              ],
            },
          ]
        : (region?.cells ?? []).map((c) => ({
            d: c.d,
            ring: [
              [c.lng, c.lat],
              [c.lng + size, c.lat],
              [c.lng + size, c.lat + size],
              [c.lng, c.lat + size],
              [c.lng, c.lat],
            ],
          }));

      const data = {
        type: "FeatureCollection" as const,
        features: rings.map((r) => ({
          type: "Feature" as const,
          properties: { d: r.d },
          geometry: { type: "Polygon" as const, coordinates: [r.ring] },
        })),
      };

      const existing = m.getSource("region") as maplibregl.GeoJSONSource | undefined;
      if (existing) {
        existing.setData(data);
        return;
      }
      m.addSource("region", { type: "geojson", data });
      m.addLayer({
        id: "region-fill",
        type: "fill",
        source: "region",
        paint: {
          "fill-color": "#35C6E8",
          "fill-opacity": [
            "interpolate",
            ["linear"],
            ["get", "d"],
            0,
            0.12,
            1,
            0.42,
          ],
        },
      });
      m.addLayer({
        id: "region-line",
        type: "line",
        source: "region",
        paint: { "line-color": "#35C6E8", "line-width": 0.5, "line-opacity": 0.5 },
      });
    };

    if (m.isStyleLoaded()) apply();
    else m.once("load", apply);
  }, [region]);

  // Lower priority than `fitTo`: a reveal or a picked spot beats the board.
  useEffect(() => {
    const m = map.current;
    if (!m || !fitBox || (fitTo && fitTo.length > 0)) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // A single-place arena would fit to a point and slam to max zoom.
    const pad = 0.35;
    m.fitBounds(
      new maplibregl.LngLatBounds(
        [fitBox.minLng - pad, fitBox.minLat - pad],
        [fitBox.maxLng + pad, fitBox.maxLat + pad],
      ),
      { padding: 46, duration: reduced ? 0 : 700, maxZoom: 9 },
    );
  }, [fitBox, fitTo]);

  useEffect(() => {
    const m = map.current;
    if (!m || !fitTo || fitTo.length === 0) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (fitTo.length === 1) {
      m.flyTo({
        center: [fitTo[0].lng, fitTo[0].lat],
        zoom: 5,
        duration: reduced ? 0 : 900,
      });
      return;
    }
    const bounds = fitTo.reduce(
      (b, p) => b.extend([p.lng, p.lat]),
      new maplibregl.LngLatBounds(
        [fitTo[0].lng, fitTo[0].lat],
        [fitTo[0].lng, fitTo[0].lat],
      ),
    );
    m.fitBounds(bounds, { padding: 80, duration: reduced ? 0 : 900, maxZoom: 7 });
  }, [fitTo]);

  const anchor = fullBleed ? ("fixed" as const) : ("absolute" as const);

  return (
    <>
      <div ref={container} style={{ position: anchor, inset: 0 }} />
      {layerToggle && (
        <button
          className="map-layer"
          style={{ position: anchor }}
          onClick={() =>
            setBasemap((b) => (b === "map" ? "satellite" : "map"))
          }
          aria-label={
            basemap === "map" ? "Switch to satellite" : "Switch to map"
          }
        >
          <span className="map-layer-icon">
            {basemap === "map" ? "🛰️" : "🗺️"}
          </span>
          <span className="map-layer-text">
            {basemap === "map" ? "Satellite" : "Map"}
          </span>
        </button>
      )}
    </>
  );
}
